/**
 * Animates SVG polygon points over a specified duration
 * @param {SVGPolygonElement} polygon - The polygon element to animate
 * @param {string} targetPoints - The final points string to animate to
 * @param {number} duration - Animation duration in milliseconds
 * @param {function} [easing] - Optional easing function
 * @returns {Promise} Resolves when animation completes
 */
function animatePolygon(polygon, targetPoints, duration, easing = t => t) {
    // Parse initial and target points into arrays of numbers
    const initialPoints = polygon.getAttribute('points')
        .trim()
        .split(/[\s,]+/)
        .map(Number);
    
    const targetPointsArr = targetPoints
        .trim()
        .split(/[\s,]+/)
        .map(Number);
    
    // Ensure both point arrays have same length
    if (initialPoints.length !== targetPointsArr.length) {
        throw new Error('Initial and target points must have the same number of coordinates');
    }
    
    // Calculate the differences between initial and target points
    const pointsDiff = targetPointsArr.map((target, i) => target - initialPoints[i]);
    
    const startTime = performance.now();
    
    return new Promise(resolve => {
        function update(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Apply easing to the progress
            const easedProgress = easing(progress);
            
            // Calculate current points
            const currentPoints = initialPoints.map((initial, i) => 
                initial + (pointsDiff[i] * easedProgress)
            );
            
            // Update polygon points
            polygon.setAttribute('points', 
                currentPoints
                    .reduce((acc, point, i) => {
                        // Add space after every second number (x,y pairs)
                        const separator = i % 2 === 1 ? ' ' : ',';
                        return acc + point + separator;
                    }, '')
                    .trim()
            );
            
            if (progress < 1) {
                requestAnimationFrame(update);
            } else {
                resolve();
            }
        }
        
        requestAnimationFrame(update);
    });
}

export {animatePolygon}